<script setup lang="ts">
import { Info } from 'lucide';
import { computed, onMounted, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphField } from '@/components/base/field';
import { SphIcon } from '@/components/base/icon';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { SphSpinner } from '@/components/base/spinner';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    federationId: number
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const REGEX_PREFIX = /\d/;
const REGEX_SUFFIX = /^\d+/;

const errors = ref<Record<string, string[]>>({});
const loading = ref(false);
const pending = ref(false);
const precheckLoading = ref(false);
const maxCounter = ref<number | null>(null);
const maxMembershipNumber = ref('');
const digitsOptions = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => ({ value: n, label: String(n) }));
const membershipNumberSettings = ref({
    enabled: false,
    prefix: '',
    suffix: '',
    rangeStart: 1,
    leadingZeros: false,
    digits: 6,
    recycling: false,
    counterBasedOnPreAndSuffix: false,
});

onMounted(() => {
    load();
});

function padNumber(value: number, digits: number): string {
    return String(value).padStart(digits, '0');
}

const previewNumber = computed(() => {
    const settings = membershipNumberSettings.value;
    const padDigits = settings.leadingZeros ? settings.digits : 0;
    const counter = padNumber(settings.rangeStart, padDigits);
    return `${settings.prefix}${counter}${settings.suffix}`;
});

function validate(): boolean {
    const prefixValid = !REGEX_PREFIX.test(membershipNumberSettings.value.prefix);
    const suffixValid = !REGEX_SUFFIX.test(membershipNumberSettings.value.suffix);

    errors.value = {};

    if (!prefixValid) {
        errors.value.prefix = [i18n.t('Der Prefix darf keine Zahl enthalten.')];
    }
    if (!suffixValid) {
        errors.value.suffix = [i18n.t('Der Suffix darf nicht mit einer Zahl beginnen.')];
    }

    return prefixValid && suffixValid;
}

function onUpdatedMemberSettings() {
    validate();
}

async function onPrecheckRelevantSettingsChanged() {
    validate();
    await getMaxMembershipNumberCounter();
}

async function load(initialLoad = true) {
    if (initialLoad) {
        loading.value = true;
    }
    try {
        const response = await fetch(`/api/federation/federations/${props.federationId}/membership-number-settings`);

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const { data } = await response.json();
        membershipNumberSettings.value = {
            ...data,
            digits: data.digits ?? 6,
        };
        if (initialLoad) {
            await getMaxMembershipNumberCounter();
        }
    } catch {
        notify.error('load.error');
    } finally {
        loading.value = false;
    }
}

async function getMaxMembershipNumberCounter() {
    precheckLoading.value = true;
    try {
        const params = new URLSearchParams({
            prefix: membershipNumberSettings.value.prefix,
            suffix: membershipNumberSettings.value.suffix,
            counterBasedOnPreAndSuffix: String(membershipNumberSettings.value.counterBasedOnPreAndSuffix),
        });
        const response = await fetch(`/api/federation/federations/${props.federationId}/membership-number-settings/precheck?${params}`);

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const { data } = await response.json();
        maxCounter.value = data.maxCounter;
        maxMembershipNumber.value = data.membershipNumber;
    } catch {
        // Silently fail - this is optional info
    } finally {
        precheckLoading.value = false;
    }
}

async function save() {
    if (!validate()) {
        return;
    }

    pending.value = true;
    try {
        const response = await fetch(`/api/federation/federations/${props.federationId}/membership-number-settings`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...membershipNumberSettings.value,
                digits: membershipNumberSettings.value.leadingZeros
                    ? membershipNumberSettings.value.digits
                    : null,
            }),
        });

        if (response.status === 400) {
            const { error } = await response.json();
            errors.value = {};
            for (const detail of error.details) {
                const attributeName = detail.find((d: { key: string }) => d.key === 'attributeName')?.value;
                const message = detail.find((d: { key: string }) => d.key === 'constraintMessage')?.value;
                if (attributeName && message) {
                    errors.value[attributeName] = [message];
                }
            }
            return;
        }

        if (!response.ok) {
            throw new Error('Request failed');
        }

        notify.success('saveChanges.success');
        await load(false);
    } catch {
        notify.error('saveChanges.error');
    } finally {
        pending.value = false;
    }
}
</script>

<template>
    <div>
        <SphSpinner
            v-if="loading"
            variant="centered"
        />
        <div v-else>
            <div class="flex items-center justify-between">
                <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                    {{ i18n.t('Mitgliedsnummern') }}
                    <SphIcon
                        :icon="Info"
                        size="sm"
                        :title="i18n.t('Mitgliedsnummern werden für aktivierte Mitglieder automatisch nach dem unten definierten Schema vergeben.')"
                    />
                </h3>
                <SphButton
                    :label="i18n.t('save')"
                    variant="primary"
                    :disabled="pending"
                    @click="save()"
                />
            </div>

            <SphField
                :label="i18n.t('Mitgliedsnummern trägerweit verwalten')"
                inline
                class="my-4"
                v-slot="field"
            >
                <SphCheckbox v-bind="field" v-model="membershipNumberSettings.enabled" />
            </SphField>

            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <div class="animated fadeIn kgr-grid__cell flex flex-col gap-4">
                            <SphField
                                :label="i18n.t('Prefix')"
                                :errors="errors.prefix"
                            >
                                <SphInput
                                    v-model="membershipNumberSettings.prefix"
                                    maxlength="6"
                                    @change="onPrecheckRelevantSettingsChanged()"
                                />
                            </SphField>

                            <SphField
                                :label="i18n.t('Suffix')"
                                :errors="errors.suffix"
                            >
                                <SphInput
                                    v-model="membershipNumberSettings.suffix"
                                    maxlength="6"
                                    @change="onPrecheckRelevantSettingsChanged()"
                                />
                            </SphField>

                            <div>
                                <SphField
                                    :label="i18n.t('Nummernkreis von')"
                                    :errors="errors.rangeStart"
                                    required
                                >
                                    <SphInput
                                        v-model="membershipNumberSettings.rangeStart"
                                        type="number"
                                        :min="1"
                                        :max="99999999"
                                        maxLength="8"
                                        @change="onUpdatedMemberSettings()"
                                    />
                                </SphField>
                                <div
                                    v-if="precheckLoading || maxCounter !== null"
                                    class="text-sm text-muted-foreground mt-1"
                                >
                                    {{ i18n.t('Aktuell höchste vergebene Nummer:') }}
                                    <SphSpinner
                                        v-if="precheckLoading"
                                        variant="inline"
                                        size="xs"
                                    />
                                    <span v-else>{{ maxCounter }}<template v-if="maxMembershipNumber"> ({{ maxMembershipNumber }})</template></span>
                                </div>
                            </div>

                            <SphField
                                :label="i18n.t('Führende Nullen verwenden')"
                                inline
                                v-slot="field"
                            >
                                <SphCheckbox
                                    v-bind="field"
                                    v-model="membershipNumberSettings.leadingZeros"
                                    @update:model-value="onUpdatedMemberSettings()"
                                />
                            </SphField>

                            <SphField
                                v-if="membershipNumberSettings.leadingZeros"
                                :label="i18n.t('Stellen des Nummernkreises')"
                                class="animated fadeIn"
                            >
                                <SphSelect
                                    v-model="membershipNumberSettings.digits"
                                    :options="digitsOptions"
                                    :null-option="false"
                                    @update:model-value="onUpdatedMemberSettings()"
                                />
                            </SphField>

                            <SphField
                                :label="i18n.t('Letzte Nummer des Mitglieds bei zusätzlichem Vertrag wiederverwenden')"
                                inline
                                v-slot="field"
                            >
                                <SphCheckbox
                                    v-bind="field"
                                    v-model="membershipNumberSettings.recycling"
                                />
                            </SphField>

                            <SphField
                                :label="i18n.t('Nummernkreis für jeden Pre- oder Suffix verwenden (nur relevant bei Änderung des Pre- oder Suffixes)')"
                                inline
                                v-slot="field"
                            >
                                <SphCheckbox
                                    v-bind="field"
                                    v-model="membershipNumberSettings.counterBasedOnPreAndSuffix"
                                    @update:model-value="onPrecheckRelevantSettingsChanged()"
                                />
                            </SphField>
                        </div>
                    </div>

                    <div class="animated fadeIn kgr-grid__cell">
                        <div class="kgr-row kgr-region kgr-region--rounded kgr-region--gray">
                            <div class="kgr-row">
                                {{ i18n.t('So sieht deine Mitgliedsnummer aus') }}:
                            </div>
                            <SphSpinner
                                v-if="loading"
                                variant="inline"
                            />
                            <span
                                v-else
                                class="kgr-text kgr-text--bold"
                            >
                                {{ previewNumber }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end mt-4">
                <SphButton
                    :label="i18n.t('save')"
                    variant="primary"
                    :disabled="pending"
                    @click="save()"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.sph-select {
    width: 100%;

    :deep(> div) {
        width: 100%;
    }
}
</style>
